import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Package, Truck, CheckCircle2, MapPin, ExternalLink, Clock, PackageCheck, Star } from "lucide-react";
import { toast } from "sonner";

function img(p) {
  if (!p) return "https://static.prod-images.emergentagent.com/jobs/d3a3c830-7d3a-49ef-ad18-3f342dc4beda/images/61fe03154047828097ebbc7961c5f590c8897894b9ea6fa9e207bb0d1fee5801.png";
  if (typeof p === "string" && p.startsWith("http")) return p;
  return fileUrl(p);
}

const STEPS = [
  { key: "paid", label: "Paid", Icon: CheckCircle2 },
  { key: "shipped", label: "Shipped", Icon: Truck },
  { key: "delivered", label: "Delivered", Icon: Package },
];

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [confirming, setConfirming] = useState(false);
  const nav = useNavigate();

  const load = () => api.get(`/orders/${id}`).then(r => setOrder(r.data)).catch((e) => setErr(e.response?.data?.detail || "Not found"));
  useEffect(() => { load(); }, [id]);

  const confirmReceipt = async () => {
    setConfirming(true);
    try {
      await api.post(`/orders/${id}/confirm-receipt`);
      toast.success("Marked as received");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm");
    } finally { setConfirming(false); }
  };

  if (err) return <div className="pt-32 text-center text-zinc-400" data-testid="order-detail-error">{err}</div>;
  if (!order) return <div className="pt-32 text-center text-zinc-500" data-testid="order-detail-loading">Loading…</div>;

  const currentIdx = STEPS.findIndex(s => s.key === order.status);
  const stepIdx = currentIdx < 0 ? 0 : currentIdx;

  return (
    <div data-testid="order-detail-page" className="pt-28 pb-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <button onClick={() => nav(-1)} data-testid="back-orders" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-2 mb-8"><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Order</p>
          <h1 data-testid="order-id" className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">#{order.id.slice(0, 8).toUpperCase()}</h1>
          <p className="text-xs text-zinc-500 mt-2">Placed {new Date(order.created_at).toLocaleString()}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Total</p>
          <p className="font-display font-black text-3xl text-[#FF007F] text-glow-pink">${Number(order.total).toFixed(2)}</p>
        </div>
      </div>

      {/* Timeline */}
      <div className="card-surface p-6 mb-8" data-testid="order-timeline">
        <div className="flex items-center justify-between gap-3">
          {STEPS.map((s, i) => {
            const active = i <= stepIdx;
            const Icon = s.Icon;
            return (
              <React.Fragment key={s.key}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 flex items-center justify-center border ${active ? "border-[#FF007F] text-[#FF007F]" : "border-white/10 text-zinc-600"} ${active ? "glow-pink" : ""}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <p className={`text-[10px] uppercase tracking-widest mt-2 ${active ? "text-white" : "text-zinc-600"}`}>{s.label}</p>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < stepIdx ? "bg-[#FF007F]" : "bg-white/10"}`} />}
              </React.Fragment>
            );
          })}
        </div>

        {order.tracking && (
          <div className="mt-6 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Carrier</p>
              <p className="text-sm font-bold text-white mt-1">{order.tracking.carrier}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Tracking #</p>
              <p data-testid="order-tracking-number" className="text-sm font-mono text-zinc-200 mt-1">{order.tracking.number}</p>
            </div>
            {order.tracking.url && (
              <a data-testid="order-tracking-link" href={order.tracking.url} target="_blank" rel="noreferrer" className="btn-secondary inline-flex items-center gap-2 !py-2 !px-4 !text-[10px]">
                Track <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        {!order.tracking && order.status === "paid" && (
          <p className="text-xs text-zinc-500 mt-6 pt-6 border-t border-white/5 flex items-center gap-2"><Clock className="w-3 h-3" /> Awaiting seller dispatch — tracking will appear here.</p>
        )}

        {order.status === "shipped" && order.user_id === user?.id && (
          <div className="mt-6 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-zinc-400">Package arrived? Close out this order so the seller knows.</p>
            <button data-testid="confirm-receipt-btn" onClick={confirmReceipt} disabled={confirming} className="btn-primary inline-flex items-center gap-2 !py-2 !px-4 !text-[10px]">
              <PackageCheck className="w-3 h-3" /> {confirming ? "Confirming…" : "Mark received"}
            </button>
          </div>
        )}

        {order.status === "delivered" && (
          <div className="mt-6 pt-6 border-t border-white/5">
            <div className="flex items-center gap-2 text-xs text-[#CCFF00] mb-4">
              <CheckCircle2 className="w-3 h-3" /> Delivered — thanks for shopping the glow.
            </div>
            {order.user_id === user?.id && (order.items || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                <p className="text-xs text-zinc-400 mr-2 self-center">Loved your order?</p>
                {(order.items || []).map((it, i) => (
                  <Link
                    key={i}
                    to={`/product/${it.product_id}#review-form`}
                    data-testid={`leave-review-${it.product_id}`}
                    className="btn-secondary !py-2 !px-3 !text-[10px] inline-flex items-center gap-1 hover:!border-[#FFB800] hover:!text-[#FFB800]"
                  >
                    <Star className="w-3 h-3" /> Review {it.title.length > 24 ? it.title.slice(0, 24) + "…" : it.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Items + Shipping */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-surface p-6">
          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Items</p>
          <ul className="divide-y divide-white/5">
            {(order.items || []).map((it, i) => (
              <li key={i} className="flex items-center justify-between py-3 text-sm">
                <Link to={`/product/${it.product_id}`} className="flex items-center gap-3 hover:text-[#FF007F]" data-testid={`order-item-${it.product_id}`}>
                  <Package className="w-4 h-4 text-zinc-500" />
                  <span>{it.title}</span>
                  <span className="text-zinc-500 text-xs">× {it.qty}</span>
                </Link>
                <span className="text-zinc-400">${(Number(it.price) * Number(it.qty)).toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-surface p-6">
          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2"><MapPin className="w-3 h-3" /> Shipping to</p>
          {order.shipping_address ? (
            <address data-testid="order-shipping-address" className="not-italic text-sm leading-relaxed text-zinc-300">
              <p className="font-bold text-white">{order.shipping_address.name}</p>
              <p>{order.shipping_address.line1}</p>
              {order.shipping_address.line2 && <p>{order.shipping_address.line2}</p>}
              <p>{order.shipping_address.city}{order.shipping_address.region ? `, ${order.shipping_address.region}` : ""} {order.shipping_address.postal}</p>
              <p>{order.shipping_address.country}</p>
              {order.shipping_address.phone && <p className="text-zinc-500 mt-2">{order.shipping_address.phone}</p>}
            </address>
          ) : <p className="text-zinc-500 text-sm">No address on file.</p>}
        </div>
      </div>

      {/* Status history */}
      {order.status_history && order.status_history.length > 0 && (
        <div className="card-surface p-6 mt-6">
          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Activity</p>
          <ul className="space-y-3 text-sm">
            {order.status_history.map((h, i) => (
              <li key={i} className="flex items-center justify-between text-zinc-300">
                <span className="uppercase tracking-widest text-xs">{h.status}</span>
                <span className="text-zinc-500 text-xs">{new Date(h.at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
